import React, { useEffect, useState } from "react";
import {
  AcceptedDateTypes,
  MonthsValueOptions,
  defaultAsSelectPlaceholder,
  defaultMonthsValueOptions,
} from "../../../types/DateTypes";
import { BaseInputProps, NotApplicable } from "../../../types/InputTypes";
import {
  calculateMaxDay,
  calculateMaxMonth,
  calculateMaxYear,
  getEnrichedDate,
  getRawDate,
  isAfter,
  isBefore,
  isDateType,
  isNA,
} from "../../../utils";
import { Select, ValueOpt } from "../select";
import { AsSelectPlaceholders } from "./DateInput";
import { SelectsHolder } from "./DateInputStyles";

export type DateInputAsSelectProps<DateType extends AcceptedDateTypes> = BaseInputProps & {
  value?: DateType | NotApplicable;
  onChange: (value?: DateType | NotApplicable) => void;
  setValidDate: (value: boolean) => void;
  minDate?: AcceptedDateTypes;
  maxDate?: AcceptedDateTypes;
  asSelectsPlaceholders?: AsSelectPlaceholders;
  monthsValueOptions?: MonthsValueOptions;
  clearable?: boolean;
  required?: boolean;
};

export const DateInputAsSelects = <DateType extends AcceptedDateTypes>(props: DateInputAsSelectProps<DateType>) => {
  const {
    value,
    onChange,
    disabled = false,
    minDate,
    maxDate,
    asSelectsPlaceholders = defaultAsSelectPlaceholder,
    monthsValueOptions = defaultMonthsValueOptions,
    clearable = false,
    required = false,
    setValidDate,
  } = props;
  const [daySelectValue, setDaySelectValue] = useState<ValueOpt<number> | undefined>(undefined);
  const [monthSelectValue, setMonthSelectValue] = useState<ValueOpt<string> | undefined>(undefined);
  const [yearSelectValue, setYearSelectValue] = useState<ValueOpt<number> | undefined>(undefined);
  const [yearOptions, setYearOptions] = useState<ValueOpt<number>[]>([]);
  const [monthOptions, setMonthOptions] = useState<ValueOpt<string>[]>([]);
  const [dayOptions, setDayOptions] = useState<ValueOpt<number>[]>([]);

  const onChangeValue = (newValue: Date | undefined | NotApplicable) => {
    onChange(getEnrichedDate(newValue, value));
  };

  const selectOnChange = (day?: ValueOpt<number>, month?: ValueOpt<string>, year?: ValueOpt<number>) => {
    if (!!day && !!month && !!year) {
      const monthIndex: number = monthsValueOptions?.findIndex((value) => value === month?.value);
      const newDate: Date = new Date(year.value, monthIndex, day.value);
      if (!!maxDate && !!minDate) {
        if (isAfter(newDate, minDate) && isBefore(newDate, maxDate)) {
          onChangeValue(newDate);
          setValidDate(true);
        } else {
          setValidDate(false);
        }
      } else if (!!maxDate) {
        if (isBefore(newDate, maxDate)) {
          onChangeValue(newDate);
          setValidDate(true);
        } else {
          setValidDate(false);
        }
      } else if (!!minDate) {
        if (isAfter(newDate, minDate)) {
          onChangeValue(newDate);
          setValidDate(true);
        } else {
          setValidDate(false);
        }
      } else {
        onChangeValue(newDate);
        setValidDate(true);
      }
    } else {
      onChangeValue(undefined);
      setValidDate(true);
    }
  };

  const changeDay = (day?: ValueOpt<number> | ValueOpt<number>[]) => {
    setDaySelectValue(day as ValueOpt<number>);
    selectOnChange(day as ValueOpt<number>, monthSelectValue, yearSelectValue);
  };

  const getDayValue = (value?: AcceptedDateTypes): ValueOpt<number> | undefined => {
    if (!!value) {
      const day = getRawDate(value)?.getDate();
      return dayOptions.find((d) => day === d.value);
    } else {
      return undefined;
    }
  };

  const changeMonth = (month?: ValueOpt<string> | ValueOpt<string>[]) => {
    setMonthSelectValue(month as ValueOpt<string>);
    setDaySelectValue(undefined);
    selectOnChange(undefined, month as ValueOpt<string>, yearSelectValue);
  };

  const getMonthValue = (value?: AcceptedDateTypes): ValueOpt<string> | undefined => {
    if (!!value) {
      const monthIndex = getRawDate(value)?.getMonth();
      if (monthIndex != undefined) {
        const month = monthsValueOptions[monthIndex];
        return monthOptions.find((m) => month === m.value);
      }
    }
    return undefined;
  };

  const changeYear = (year?: ValueOpt<number> | ValueOpt<number>[]) => {
    setYearSelectValue(year as ValueOpt<number>);
    setMonthSelectValue(undefined);
    setDaySelectValue(undefined);
    selectOnChange(undefined, undefined, year as ValueOpt<number>);
  };

  const getYearValue = (value?: AcceptedDateTypes): ValueOpt<number> | undefined => {
    if (!!value) {
      const year = getRawDate(value)?.getFullYear();
      return yearOptions.find((y) => year === y.value);
    } else {
      return undefined;
    }
  };

  const isDate: boolean = isDateType(value);
  const isNAValue: boolean = isNA(!isDate, value);

  const calculateSelectValues = (
    shouldFilter: boolean,
    monthsValueOptions: MonthsValueOptions,
    yearSelectValue?: ValueOpt<number>,
    monthSelectValue?: ValueOpt<string>,
    maxDate?: AcceptedDateTypes,
    minDate?: AcceptedDateTypes,
  ) => {
    if (value === "N/A") {
      return;
    } else {
      const newMonthOptions = calculateMaxMonth(shouldFilter, monthsValueOptions, yearSelectValue, minDate, maxDate);
      const newDayOptions = calculateMaxDay(
        shouldFilter,
        monthsValueOptions,
        yearSelectValue,
        monthSelectValue,
        minDate,
        maxDate,
      );
      const newYearOptions = calculateMaxYear(shouldFilter, minDate, maxDate);
      if (JSON.stringify(newMonthOptions) !== JSON.stringify(monthOptions)) {
        setMonthOptions(newMonthOptions);
      }
      if (JSON.stringify(newDayOptions) !== JSON.stringify(dayOptions)) {
        setDayOptions(newDayOptions);
      }
      if (JSON.stringify(newYearOptions) !== JSON.stringify(yearOptions)) {
        setYearOptions(newYearOptions);
      }
    }
  };

  useEffect(() => {
    if (!!value && isDate) {
      const newDayValue = getDayValue(value);
      const newMonthValue = getMonthValue(value);
      const newYearValue = getYearValue(value);
      if (newDayValue !== daySelectValue) {
        setDaySelectValue(newDayValue);
      }
      if (newMonthValue !== monthSelectValue) {
        setMonthSelectValue(newMonthValue);
      }
      if (newYearValue !== yearSelectValue) {
        setYearSelectValue(newYearValue);
      }
    }
  }, [value, isDate, yearOptions, monthOptions, dayOptions]);

  useEffect(() => {
    calculateSelectValues(true, monthsValueOptions, yearSelectValue, monthSelectValue, maxDate, minDate);
  }, [monthsValueOptions, maxDate, minDate, yearSelectValue, monthSelectValue]);

  return (
    <SelectsHolder>
      <Select
        id="year"
        placeholder={asSelectsPlaceholders.year}
        className="me-2"
        value={yearSelectValue}
        onChange={changeYear}
        options={yearOptions}
        clearable={clearable}
        required={required}
        disabled={disabled || isNAValue}
      />
      <Select
        id="month"
        placeholder={asSelectsPlaceholders.month}
        className="me-2"
        value={monthSelectValue}
        onChange={changeMonth}
        options={monthOptions}
        clearable={clearable}
        required={required}
        disabled={disabled || isNAValue || !yearSelectValue}
      />
      <Select
        id="day"
        placeholder={asSelectsPlaceholders.day}
        className="me-2"
        value={daySelectValue}
        onChange={changeDay}
        options={dayOptions}
        disabled={disabled || isNAValue || !monthSelectValue}
        clearable={clearable}
        required={required}
      />
    </SelectsHolder>
  );
};

export default DateInputAsSelects;
